<template>
  <v-app-bar
    class="app-bar"
    :height="72"
  >
    <v-container class="app-bar__wrapper">
      <div class="app-bar__logo-container mr-5">
        <router-link :to="homeRoute">
          <v-img
            src="@/images/logo.png"
            class="app-bar__logo"
            contain
            height="57"
            max-height="57"
            max-width="175"
            width="175"
          />
        </router-link>
      </div>
      <v-list
        v-if="display.smAndUp"
        class="d-flex app-bar__menu-options"
      >
        <template
          v-for="(link, index) in navigationLinks"
          :key="'app-bar-'+index"
        >
          <v-list-item
            :to="!link.action ? $routeTo(link.to) : null"
            style="min-height: 77px"
            @click="link.action ? link.action() : null"
          >
            <v-list-item-title
              class="app-bar__menu-options__title"
              v-text="link.text"
            />
          </v-list-item>
        </template>
      </v-list>
      <v-spacer />
      <v-app-bar-nav-icon
        v-if="display.mdAndDown"
        @click.stop="showNavigation = !showNavigation"
      />
      <template v-else>
        <div
          v-if="!$auth.isGuest()"
          class="pl-2 d-flex flex-column justify-space-around"
        >
          <div class="d-flex text-no-wrap align-center">
            <div
              class="d-flex text-decoration-none align-center cursor-pointer"
              title="My Profile"
              @click="profile"
            >
              <v-avatar
                class="app-bar__avatar"
                color="primary"
                size="44"
                :class="{'mr-3': display.mdAndUp, 'mr-0': display.smAndDown}"
              >
                <span
                  v-if="$auth && !$auth.user?.avatar_id"
                  class="text-white"
                >
                  {{ $auth && $auth.user ? $auth.user.initials : '' }}
                </span>
                <v-img
                  v-else
                  alt="User Avatar"
                  :src="$file.url($auth.user!.avatar_id)"
                />
              </v-avatar>
              <div
                class="app-bar__profile-info"
                :style="{'display': display.smAndDown ? 'none' : 'block'}"
              >
                <div class="font-weight-bold text-black">
                  {{ widgetName }}
                </div>
                <div class="text-black">
                  {{ $auth && $auth.isAdmin() ? 'Admin' : '' }}
                </div>
              </div>
            </div>
            <v-btn
              v-if="$auth.user?.is_impersonated"
              color="error"
              class="ml-5"
              variant="text"
              icon="mdi-account-switch"
              title="Stop Impersonating"
              @click="stopImpersonating"
            />
            <v-btn
              variant="text"
              class="app-bar__logout-button ml-5"
              color="primary"
              @click="logout"
            >
              Logout
            </v-btn>
          </div>
        </div>
        <div v-else>
          <v-btn
            variant="text"
            class="app-bar__login-button mr-4"
            color="primary"
            :height="44"
            @click="$router.push($routeTo(ROUTES.LOGIN))"
          >
            Log In
          </v-btn>
          <v-btn
            variant="flat"
            class="app-bar__signup-button"
            color="primary"
            :height="44"
            @click="$router.push($routeTo(ROUTES.REGISTER))"
          >
            Sign Up
          </v-btn>
        </div>
      </template>
    </v-container>
  </v-app-bar>
</template>

<script lang="ts">
import {defineComponent, ref} from 'vue'
import {mapWritableState} from "pinia"
import {useAppStore} from "@/stores/app"
import {useDisplay} from "vuetify"
import home from "@/mixins/home"
import profile from "@/mixins/profile"

interface Link {
  text: string
  to: string
  target?: string
  icon: string
  action?: () => void
}


export default defineComponent({
  emits: ['logout', 'stop-impersonating', 'profile'],
  mixins: [home, profile],
  props: {
    navigationLinks: {
      type: Array as () => Link[],
      required: true,
    },
  },
  setup() {
    const display = ref(useDisplay())
    return {display: display}
  },
  data() {
    return {
      maxPoNumber: 5
    }
  },
  computed: {
    ...mapWritableState(useAppStore, ['showNavigation']),
    widgetName() {
      if(this.$auth && this.$auth.user) {
        const maxChars = 15
        return this.$auth.user.full_name.length > maxChars ? this.$auth.user.full_name.substring(0, maxChars) + '...' : this.$auth.user.full_name
      }
      return ''
    },
  },
  methods: {
    async logout() {
      this.$emit('logout')
    },
    async stopImpersonating() {
      this.$emit('stop-impersonating')
    },
  },
})
</script>

<style scoped lang="scss">
.app-bar {
  margin: 0 auto;
  &__wrapper {
    display: flex;
    align-items: center;
  }
  &__menu-options {
    @media (max-width: 850px) {
      display: none!important;
    }
    &__title {
      font-size: 16px;
      font-weight: 600;
      color: #393939;
      @media (max-width: 1000px) {
        font-size: 14px;
      }
    }
    .v-list-item {
      position: relative;
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        min-height: 7px;
        max-height: 7px;
        border: unset;
        background-color: #EC5C29;
        opacity: 0;
        top: unset;
      }
    }
    .v-list-item--active {
      color: #EC5C29;
      :deep(.v-list-item__overlay) {
        background: transparent;
      }
      &::after {
        opacity: 1;
      }
    }
  }
}
</style>
